import type { RoomDef } from '../sim/room';

/**
 * The Earth tutorial (GDD: the tutorial unlocks the Heat Shield): one hand-made room from Dad's
 * back garden to the launch gantry, played once before the first launch. Each obstacle needs the
 * control the tip above it teaches: a fence to jump, a shed to fly over, the house wall to step
 * round in the lane behind, the garage to step back out of, a scout to shoot, a ditch too wide to jump (dash or fly).
 * The treehouse in the far lane hides a shard for the curious.
 * `.` empty · `#` solid · `=` one-way · `P` start · `*` Starlight cell · `m` shard ·
 * `c` crawler · `d` drone · `X` exit (the gantry).
 */
export const EARTH_TUTORIAL: RoomDef = {
  id: 'earth-tutorial',
  name: 'EARTH',
  // A lid on the garden: no flying over the house wall instead of stepping round it.
  ceiling: true,
  lanes: [
    // Lane 0
    [
      '......................................................###...............................................',
      '......................................................###...............................................',
      '......................................................###...............................................',
      '......................................................###...............................................',
      '......................................................###.............................................##',
      '......................................................###.............................................##',
      '......................................................###.............................................##',
      '.....................................**...............###.............................................##',
      '....................................####..............###.............................................##',
      '....................................####..............###.....................d.......................##',
      '....................................####..............###...........................................XX##',
      '....................................####..............###...........................................XX##',
      '..................##......##........####..............###...........................................XX##',
      '....P.............##......##........####..............###...............c..................*.*.*....XX##',
      '###################################################################################......###############',
      '###################################################################################......###############',
      '###################################################################################......###############',
    ],
    // Lane 1
    [
      '..............................................................###.......................................',
      '..............................................................###.......................................',
      '..............................................................###.......................................',
      '..............................................................###.......................................',
      '..............................................................###.....................................##',
      '..............................................................###.....................................##',
      '..............................................................###.....................................##',
      '..............................................................###.....................................##',
      '....................................####......................###.....................................##',
      '....................................####......................###.....................................##',
      '......................*.............####......................###...................................XX##',
      '....................=====...........####......................###...................................XX##',
      '....................................####..................**..###...................................XX##',
      '....................................####......................###...................................XX##',
      '###################################################################################......###############',
      '###################################################################################......###############',
      '###################################################################################......###############',
    ],
    // Lane 2
    [
      '..............................................................###.......................................',
      '..............................................................###.......................................',
      '..............................................................###.......................................',
      '..............................................................###.......................................',
      '..............................................................###.....................................##',
      '.............................................m................###.....................................##',
      '...........................................=====..............###.....................................##',
      '..............................................................###.....................................##',
      '....................................####......................###.....................................##',
      '....................................####......................###.....................................##',
      '....................................####===...................###...................................XX##',
      '....................................####......................###...................................XX##',
      '....................................####......................###...................................XX##',
      '....................................####......................###...................................XX##',
      '###################################################################################......###############',
      '###################################################################################......###############',
      '###################################################################################......###############',
    ],
  ],
};

/** One tip, shown once the player gets this far (a tile column); words for touch or keys. */
export interface TutorialTip {
  readonly col: number;
  readonly touch: string;
  readonly keys: string;
}

/** The suit's instructions, in order along the room. Clipped: its voice is cold at first. */
export const TUTORIAL_TIPS: readonly TutorialTip[] = [
  {
    col: 0,
    touch: 'MOVE: LEFT THUMB DOWN ANYWHERE ON THE LEFT, THEN SLIDE',
    keys: 'MOVE: ARROWS OR A / D',
  },
  { col: 12, touch: 'JUMP: TAP THE BIG BUTTON', keys: 'JUMP: SPACE' },
  {
    col: 30,
    touch: 'TOO HIGH. JUMP, THEN HOLD THE BUTTON IN THE AIR TO FLY',
    keys: 'TOO HIGH. JUMP, THEN HOLD SPACE IN THE AIR TO FLY',
  },
  {
    col: 41,
    touch: 'FLYING BURNS STARLIGHT (TOP LEFT). IT REFILLS ON THE GROUND',
    keys: 'FLYING BURNS STARLIGHT (TOP LEFT). IT REFILLS ON THE GROUND',
  },
  {
    col: 48,
    touch: 'BLOCKED. SWIPE UP ON THE RIGHT TO STEP INTO THE BACKGROUND',
    keys: 'BLOCKED. Q STEPS INTO THE BACKGROUND',
  },
  {
    col: 57,
    touch: 'BLOCKED AGAIN. SWIPE DOWN TO STEP BACK OUT',
    keys: 'BLOCKED AGAIN. E STEPS BACK OUT',
  },
  {
    col: 65,
    touch: 'A SCOUT FOLLOWED US. FIRE: THE SMALL BUTTON. IT AIMS FOR YOU',
    keys: 'A SCOUT FOLLOWED US. FIRE: J. IT AIMS FOR YOU',
  },
  {
    col: 74,
    touch: 'HOLD FIRE, THEN LET GO: A ROCKET. IT COSTS STARLIGHT',
    keys: 'HOLD J, THEN LET GO: A ROCKET. IT COSTS STARLIGHT',
  },
  {
    col: 80,
    touch: 'TOO WIDE TO JUMP. JUMP, THEN FLICK THE STICK TWICE TO DASH',
    keys: 'TOO WIDE TO JUMP. JUMP, THEN SHIFT TO DASH',
  },
  { col: 89, touch: 'STARLIGHT CELLS REFILL THE SUIT', keys: 'STARLIGHT CELLS REFILL THE SUIT' },
  {
    col: 96,
    touch: 'THE HEAT SHIELD IS IN THE GANTRY. STEP IN TO LAUNCH',
    keys: 'THE HEAT SHIELD IS IN THE GANTRY. STEP IN TO LAUNCH',
  },
];

/** The tip for the farthest column the player has reached. */
export function tutorialTip(farthestCol: number): TutorialTip | null {
  let tip: TutorialTip | null = null;
  for (const t of TUTORIAL_TIPS) if (t.col <= farthestCol) tip = t;
  return tip;
}
